import { logger } from "@/utils/logger";
import React from 'react';
import { Button } from '@/components/ui/button';
import { ExternalLink } from 'lucide-react';
import DOMPurify from 'dompurify';
import { openURL } from '@/utils/navigationUtils';
import { decodeText } from '@/utils/textDecoding';

interface TextWithLinksMessageProps {
  content: string;
  isUser: boolean;
}

interface ParsedContent {
  type: 'text' | 'button';
  content: string;
  link?: string;
  target?: string;
}

const TextWithLinksMessage: React.FC<TextWithLinksMessageProps> = ({ content, isUser }) => {
  if (!content.trim()) return null;


  const decodedContent = decodeText(content);

  // Clean YouTube URLs by removing &feature=youtu.be parameter
  const cleanYouTubeUrl = (url: string): string => {
    if (url.includes('youtube.com') || url.includes('youtu.be')) {
      return url.replace(/&feature=youtu\.be/g, '');
    }
    return url;
  };

  // Parse markdown-style links [text](link) OR [text]url and bold **text** formatting
  const parseMarkdownLinks = (text: string): ParsedContent[] => {
    const parts: ParsedContent[] = [];
    // Regex handles both [text](url) and [text]url formats
    const linkRegex = /\[([^\]]+)\](?:\(([^)]+)\)(?:\{:target="([^"]+)"\})?|(https?:\/\/[^\s\]]+))/g;
    let lastIndex = 0;
    let match;

    while ((match = linkRegex.exec(text)) !== null) {
      if (match.index > lastIndex) {
        let textBefore = text.slice(lastIndex, match.index);
        // Remove trailing arrow (→) and surrounding whitespace/dashes before a link button
        textBefore = textBefore.replace(/→[\s—–-]*$/g, '').trimEnd();
        if (textBefore.trim()) {
          parts.push({ type: 'text', content: parseBoldText(textBefore) });
        }
      }

      const url = match[2] || match[4];
      parts.push({
        type: 'button',
        content: match[1],
        link: cleanYouTubeUrl(url),
        target: match[3]
      });

      lastIndex = match.index + match[0].length;
    }

    if (lastIndex < text.length) {
      const remainingText = text.slice(lastIndex);
      if (remainingText.trim()) {
        parts.push({ type: 'text', content: parseBoldText(remainingText) });
      }
    }

    if (parts.length === 0) {
      parts.push({ type: 'text', content: parseBoldText(text) });
    }

    return parts;
  };

  // Parse **bold** text and convert to HTML
  const parseBoldText = (text: string): string => {
    logger.log('🎯 [parseBoldText] Input:', text.substring(0, 150));

    // Special handling for arrow bullets: '**→** Some text' => bold arrow + text
    const arrowBulletRegex = /\*\*→\*\*\s*([^\n]+)/g;
    let processed = text.replace(arrowBulletRegex, '<strong>→ $1</strong>');

    // Generic markdown bold handling for remaining patterns
    const result = processed.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
    logger.log('🎯 [parseBoldText] Output:', result.substring(0, 150));
    return result;
  };

  const parsedContent = parseMarkdownLinks(decodedContent);

  // Helper function to check if URL is external
  const isExternalLink = (url: string): boolean => {
    try {
      const linkUrl = new URL(url, window.location.href);
      return linkUrl.hostname !== window.location.hostname;
    } catch {
      return false;
    }
  };

  return (
    <div className={`rounded-lg px-4 py-2 max-w-full min-w-0 mb-2 ${
      isUser
        ? 'bg-gray-600 text-white rounded-br-sm'
        : 'bg-gray-100 text-gray-800 rounded-bl-sm'
    }`}>
      <div className="space-y-2">
        {parsedContent.map((part, index) => {
          if (part.type === 'button' && part.link) {
            const isExternal = isExternalLink(part.link);
            const shouldOpenInNewTab = part.target === '_blank' || isExternal;
            
            const handleClick = () => {
              if (shouldOpenInNewTab) {
                // Open in new tab for external or explicitly marked links
                window.open(part.link, '_blank', 'noopener,noreferrer');
              } else {
                // Use existing openURL function for same-domain links
                openURL(part.link);
              }
            };

            return (
              <div key={index} className="my-2">
                <Button
                  variant="outline"
                  className="w-full h-auto py-3 px-4 bg-white hover:bg-gray-50 text-gray-900 d2cbot-bot_border-gray-200 whitespace-normal"
                  onClick={handleClick}
                >
                  <div className="flex items-center gap-3 w-full min-w-0">
                    <ExternalLink className="w-5 h-5 text-gray-600 flex-shrink-0" />
                    <span className="font-medium break-words text-left flex-1 min-w-0">{part.content}</span>
                  </div>
                </Button>
              </div>
            );
          } else {
            let processedContent = part.content;
            
            // Convert double newlines to line breaks for paragraph spacing
            processedContent = processedContent.replace(/\n\n/g, '<br><br>');
            
            // Convert single newlines to single line breaks
            processedContent = processedContent.replace(/\n/g, '<br>');

            // Sanitize HTML content to prevent XSS attacks
            const sanitizeConfig = {
              ALLOWED_TAGS: ['br', 'p', 'strong', 'em', 'u', 'b', 'i', 'img'],
              ALLOWED_ATTR: ['src', 'alt', 'class', 'style']
            };
            
            const sanitizedContent = DOMPurify.sanitize(processedContent, sanitizeConfig);
            logger.log('🎯 [TextWithLinksMessage] After sanitize:', sanitizedContent.substring(0, 150));
            
            
            return (
              <div
                key={index}
                className="whitespace-pre-wrap break-words"
                style={{ wordBreak: 'break-word', overflowWrap: 'anywhere' }}
                dangerouslySetInnerHTML={{ __html: sanitizedContent }}
              />
            );
          }
        })}
      </div>
    </div>
  );
};

export default TextWithLinksMessage;